/* Xiaomi 2026 · Partner Quick Preview Modal · Ayush image-3 aesthetic
 *
 * <PartnerQuickPreview retailerId="200167" onClose={...} onOpenFull={(id)=>...} />
 *
 * Layout per image 3:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ [PB] Owner Name                                            × │
 *   │      Outlet · City                            Luxe          │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ ┌─Phone─┐ ┌─Language─┐ ┌─Engagement─┐                       │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Birthday Gift · Family Photoshoot · Anniversary · Dinner ··  │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Timeline (selected benefit)         │ WhatsApp preview pane  │
 *   │  · Queued    ✓                       │ ┌──────────────────┐  │
 *   │  · On the way ✓                      │ │ via WhatsApp · TE│  │
 *   │  · Delivered ✓                       │ │  Template body   │  │
 *   │  · Confirmed ⏳                      │ └──────────────────┘  │
 *   │ [Send via Zoho]  [📲 WhatsApp]      │                        │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Open full 360 →                                              │
 *   └──────────────────────────────────────────────────────────────┘
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MessageComposerModal from './MessageComposerModal';
import EditablePref from './EditablePref';

// Each benefit tab maps to a dotted-path on `preferences_2026.*` where
// Ayush stores the partner-specific venue/option text. Anniversary,
// Dinner and Holiday reuse the existing v5-Excel fields that Ayush's
// Anniversary Push page already binds to, so editing in either place
// flows to the same source-of-truth.
const BENEFIT_PREF_PATH = {
  'xiaomi_b01_onboarding_gift':              'preferences_2026.onboarding_gift',
  'xiaomi_b06_birthday_gift':                'preferences_2026.birthday_gift',
  'xiaomi_b04_professional_family_photoshoot': 'preferences_2026.family_photoshoot',
  'xiaomi_b08_anniversary_staycation':       'preferences_2026.weekend_anniversary_dinner',
  'xiaomi_b10_dinner_2':                     'preferences_2026.dinner_4star',
  'xiaomi_b11_dayout':                       'preferences_2026.day_out_plan',
  'xiaomi_b09_domestic_holiday':             'preferences_2026.holiday_ooty_first',
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const TIER_TONE = {
  'Platinum Luxe':      { bg: '#fde68a', border: '#ca8a04', tone: '#713f12' },
  'Platinum Signature': { bg: '#f5d0fe', border: '#a855f7', tone: '#581c87' },
  'Platinum Prime':     { bg: '#bfdbfe', border: '#3b82f6', tone: '#1e3a8a' },
  'Rising Platinum':    { bg: '#bbf7d0', border: '#16a34a', tone: '#14532d' },
};

const BENEFIT_TABS = [
  { id: 'xiaomi_b01_onboarding_gift',           label: 'Onboarding gift',  icon: '🎁' },
  { id: 'xiaomi_b06_birthday_gift',             label: 'Birthday gift',    icon: '🎂' },
  { id: 'xiaomi_b04_professional_family_photoshoot', label: 'Family photoshoot', icon: '📸' },
  { id: 'xiaomi_b08_anniversary_staycation',    label: 'Anniversary',      icon: '💍' },
  { id: 'xiaomi_b10_dinner_2',                  label: 'Dinner',           icon: '🍽️' },
  { id: 'xiaomi_b11_dayout',                    label: 'Day out',          icon: '🎢' },
  { id: 'xiaomi_b09_domestic_holiday',          label: 'Holiday',          icon: '✈️' },
];

// Map workflow-engine step (1-15) → simplified Ayush 4-step display
const STEP_TO_STAGE = (step) => {
  if (step <= 1) return 'queued';
  if (step <= 6) return 'on_the_way';
  if (step <= 10) return 'delivered';
  return 'confirmed';
};


function initials(name) {
  return (name || '?').split(' ').filter(Boolean).slice(0, 2)
    .map((x) => x[0]).join('').toUpperCase();
}


function StatTile({ label, value, hint, testId }) {
  return (
    <div className="x-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-soft">{label}</div>
      <div className="text-base font-semibold text-[#15110d] mt-0.5 truncate">{value || '—'}</div>
      {hint && <div className="text-[10px] text-soft mt-0.5">{hint}</div>}
    </div>
  );
}


function StepDot({ active, complete }) {
  if (complete) return <div className="w-6 h-6 rounded-full bg-[#166534] text-white text-[12px] flex items-center justify-center">✓</div>;
  if (active) return <div className="w-6 h-6 rounded-full bg-[#ff6900] text-white text-[12px] flex items-center justify-center animate-pulse">●</div>;
  return <div className="w-6 h-6 rounded-full border-2 border-line bg-white" />;
}


function JourneyTimeline({ instance }) {
  const steps = [
    { key: 'queued', label: 'Queued', sub: 'Auto-created by engine' },
    { key: 'on_the_way', label: 'On the way', sub: 'WA fired · LSM coordinating' },
    { key: 'delivered', label: 'Delivered', sub: 'Gift / experience completed' },
    { key: 'confirmed', label: 'Confirmed', sub: 'Feedback captured · journey closed' },
  ];

  const currentStage = instance ? STEP_TO_STAGE(instance.current_step || 1) : null;
  const currentIdx = steps.findIndex((s) => s.key === currentStage);

  return (
    <div data-testid="journey-timeline">
      {steps.map((s, i) => {
        const complete = currentIdx > i;
        const active = currentIdx === i;
        return (
          <div key={s.key} className="flex items-start gap-3 mb-3" data-testid={`timeline-step-${s.key}`}>
            <div className="flex flex-col items-center">
              <StepDot active={active} complete={complete} />
              {i < steps.length - 1 && (
                <div className={`w-0.5 h-8 mt-1 ${complete ? 'bg-[#166534]' : 'bg-line'}`} />
              )}
            </div>
            <div className={`pt-0.5 ${active ? '' : (complete ? 'opacity-90' : 'opacity-50')}`}>
              <div className="text-sm font-semibold text-[#15110d]">{s.label}</div>
              <div className="text-[11px] text-soft">{s.sub}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function WAPreviewPane({ benefitId, language, draftOverride }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/xiaomi/templates`, auth())
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  // Heuristic: pick templates for the benefit + matching language (then en fallback)
  const matched = useMemo(() => {
    const lang = (language || 'en').toLowerCase();
    return templates
      .filter((t) => {
        const k = (t.template_key || '').toLowerCase();
        const benefitShort = (benefitId || '').replace('xiaomi_', '').replace(/^b\d+_/, '');
        const benefitMatch = k.includes(benefitShort) ||
          (benefitId || '').includes(k);
        const langMatch = (t.language || '').toLowerCase() === lang;
        return benefitMatch && langMatch;
      })
      .slice(0, 2);
  }, [templates, benefitId, language]);

  // When a draft has been staged from the composer, REPLACE the template
  // preview with Ayush's draft so he can review-in-context before sending.
  if (draftOverride) {
    return (
      <div
        className="rounded-2xl p-4 overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg,#7c2d12,#9a3412)',
          minHeight: '240px',
          maxHeight: '420px',
          border: '2px dashed #fb923c',
        }}
        data-testid="wa-preview-pane-draft"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-[#fb923c] animate-pulse" />
          <div className="text-[10px] uppercase tracking-wider text-[#fed7aa]">
            DRAFT · {draftOverride.target_language_name} · NOT SENT
          </div>
        </div>
        <div className="bg-white/95 rounded-lg p-3 text-[13px] text-[#15110d] whitespace-pre-wrap leading-relaxed"
          data-testid="wa-draft-body">
          {draftOverride.translated_text}
        </div>
        <details className="mt-2">
          <summary className="text-[10px] text-[#fed7aa] cursor-pointer hover:text-white">
            ▸ English source
          </summary>
          <div className="text-[11px] text-[#fef3c7] mt-1 italic whitespace-pre-wrap"
            data-testid="wa-draft-source">
            {draftOverride.source_text}
          </div>
        </details>
        <div className="text-[9px] text-[#fed7aa] mt-2">
          Press <strong>🚀 Send to Zoho</strong> below to dispatch.
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-4 overflow-y-auto"
      style={{
        background: 'linear-gradient(180deg,#0f172a,#1e293b)',
        minHeight: '240px',
        maxHeight: '420px',
      }}
      data-testid="wa-preview-pane"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-[#22c55e]" />
        <div className="text-[10px] uppercase tracking-wider text-[#94a3b8]">
          via WhatsApp · {(language || 'en').toUpperCase()}
        </div>
      </div>
      {loading && <div className="text-[#94a3b8] text-sm">Loading templates…</div>}
      {!loading && matched.length === 0 && (
        <div className="text-[#94a3b8] text-sm">
          No approved template yet for this benefit in {(language || 'en').toUpperCase()}.
          <div className="text-[11px] mt-1 text-[#64748b]">
            Submit via /xiaomi/admin/templates → Meta approval (1-7d)
          </div>
        </div>
      )}
      {!loading && matched.length > 0 && (
        <div className="space-y-2" data-testid="wa-preview-bubbles">
          {matched.map((t, i) => (
            <div
              key={i}
              className="rounded-lg p-3 max-w-[85%]"
              style={{
                background: '#065f46',
                color: '#ecfdf5',
              }}
            >
              <div className="text-[10px] text-[#a7f3d0] mb-1">
                {t.template_key} · {t.status}
              </div>
              <div className="text-[13px] whitespace-pre-wrap">
                {(t.body || '').slice(0, 280)}
                {(t.body || '').length > 280 && '…'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function PartnerQuickPreview({ retailerId, onClose, onOpenFull }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(BENEFIT_TABS[1].id); // default: Birthday gift
  const [composerInstance, setComposerInstance] = useState(null);
  const [talkNowOpen, setTalkNowOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Live draft staging area · when set, the green WhatsApp box on the
  // right shows THIS instead of the AI-drafted template, and a "Send to
  // Zoho" button appears below.  Cleared once dispatched or discarded.
  // Shape: { source_text, translated_text, target_language,
  //          target_language_name, benefit_id, benefit_label }
  const [liveDraft, setLiveDraft] = useState(null);
  const [sendingDraft, setSendingDraft] = useState(false);

  useEffect(() => {
    if (!retailerId) return;
    setLoading(true);
    axios.get(`${API}/xiaomi/dealers/${retailerId}/360`, auth())
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [retailerId]);

  const downloadProfilePdf = async () => {
    if (!data) return;
    setDownloadingPdf(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/dealers/${encodeURIComponent(data.retailer_id)}/profile.pdf`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (data.identity.outlet_name || data.retailer_id)
        .replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
      a.href = url;
      a.download = `xiaomi_partner_${data.retailer_id}_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Profile PDF downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate PDF');
    } finally { setDownloadingPdf(false); }
  };

  /**
   * Two-step dispatch flow (matches Dipali's stated UX):
   *   1. Modal Translate → calls `onDraftReady(...)` which we wire here
   *      to set `liveDraft`.  Modal closes.
   *   2. Green WA box now shows the draft + a Send/Discard pair under it.
   *   3. Send → POST /dealer/{id} · saves + tries Zoho push · clears draft.
   *   4. Discard → just clears liveDraft.
   */
  const sendDraftToZoho = async () => {
    if (!data || !liveDraft) return;
    setSendingDraft(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/messages/dealer/${encodeURIComponent(data.retailer_id)}`,
        {
          source_text: liveDraft.source_text,
          translated_text: liveDraft.translated_text,
          target_language: liveDraft.target_language,
          target_language_name: liveDraft.target_language_name,
          instance_id: activeInstance?._id || null,
          benefit_id: liveDraft.benefit_id || null,
          benefit_label: liveDraft.benefit_label || null,
          auto_push_to_zoho: true,
        },
        auth(),
      );
      const z = r.data.zoho || {};
      if (z.status === 'pushed') {
        toast.success('Sent · landed on Zoho ticket');
      } else if (z.status === 'queued_local') {
        toast.success('Sent · queued locally (Zoho will flush when bridge is live)');
      } else {
        toast.success('Sent');
      }
      setLiveDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSendingDraft(false); }
  };

  // Resolve the matching active instance for the selected tab
  const activeInstance = useMemo(() => {
    if (!data) return null;
    return (data.active_instances || []).find((i) => i.benefit_id === tab);
  }, [data, tab]);

  // Determine which benefits this partner is eligible for · disable non-eligible tabs
  const eligibilityMap = useMemo(() => {
    if (!data) return {};
    const m = {};
    for (const e of (data.eligibility || [])) {
      m[e.benefit_id] = e.eligible;
    }
    return m;
  }, [data]);

  if (!retailerId) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="quick-preview-overlay"
    >
      <div
        className="bg-[#fbfaf7] w-full max-w-5xl rounded-2xl overflow-hidden flex flex-col shadow-2xl"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-preview-modal"
      >
        {loading && (
          <div className="p-12 text-center text-soft" data-testid="preview-loading">
            Loading partner…
          </div>
        )}
        {!loading && !data && (
          <div className="p-12 text-center text-soft">Could not load partner.</div>
        )}
        {!loading && data && (
          <>
            {/* STICKY UPPER · header + stats + directive + tabs */}
            <div className="flex-shrink-0">
              {/* Header */}
              <div className="px-5 pt-4 pb-3 border-b border-line">
                <div className="flex items-start gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg,#15110d,#2a2419)' }}
                  >
                    {initials(data.identity.owner_name || data.identity.outlet_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{
                          backgroundColor: (TIER_TONE[data.identity.tier] || {}).bg || '#f3f4f6',
                          border: `1px solid ${(TIER_TONE[data.identity.tier] || {}).border || '#9ca3af'}`,
                          color: (TIER_TONE[data.identity.tier] || {}).tone || '#374151',
                        }}
                      >
                        ★ {(data.identity.tier || '').replace('Platinum ', '')}
                      </span>
                      <span className="text-[10px] text-soft font-mono">#{data.retailer_id}</span>
                    </div>
                    <h2 className="text-lg leading-tight" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
                      {data.identity.owner_name || data.identity.outlet_name}
                    </h2>
                    <div className="text-[11px] text-soft truncate">
                      {data.identity.outlet_name} · {data.identity.city || '—'}
                    </div>
                  </div>
                  <button onClick={onClose}
                    className="text-2xl text-soft hover:text-ink leading-none flex-shrink-0 px-2"
                    data-testid="quick-preview-close">×</button>
                </div>

                {/* Quick reach-out chips + Xiaomi chain · NEW */}
                <div className="mt-2.5 flex flex-wrap gap-1.5 items-center"
                  data-testid="quick-preview-reachout-bar">
                  {data.identity.phone_primary && (
                    <a href={`tel:${data.identity.phone_primary}`}
                      className="px-2 py-0.5 rounded bg-[#dbeafe] text-[#1e40af] hover:bg-[#bfdbfe] text-[10px] font-mono"
                      data-testid="qp-call-partner">
                      📞 {data.identity.phone_primary}
                    </a>
                  )}
                  {data.identity.phone_primary && (
                    <a href={`https://wa.me/${(data.identity.phone_primary || '').replace(/[^\d]/g,'')}`}
                      target="_blank" rel="noreferrer"
                      className="px-2 py-0.5 rounded bg-[#dcfce7] text-[#15803d] hover:bg-[#bbf7d0] text-[10px]"
                      data-testid="qp-wa-partner">
                      💬 WA
                    </a>
                  )}
                  {data.identity.email_primary && (
                    <a href={`mailto:${data.identity.email_primary}`}
                      className="px-2 py-0.5 rounded bg-[#fef3c7] text-[#854d0e] hover:bg-[#fde68a] text-[10px] truncate max-w-[170px]"
                      data-testid="qp-email-partner">
                      ✉ Email
                    </a>
                  )}
                  {/* ZSM */}
                  {data.identity.zsm_name && (
                    <span className="text-[10px] text-soft ml-1">·</span>
                  )}
                  {data.identity.zsm_name && (
                    <span className="text-[10px] text-soft">
                      👔 ZSM <strong className="text-[#15110d]">{data.identity.zsm_name}</strong>
                    </span>
                  )}
                  {data.identity.zsm_mobile && (
                    <a href={`tel:${data.identity.zsm_mobile}`}
                      className="px-1.5 py-0.5 rounded bg-[#fff7ed] text-[#7c2d12] hover:bg-[#ffedd5] text-[10px] font-mono"
                      data-testid="qp-call-zsm"
                      title="Call ZSM">
                      📞
                    </a>
                  )}
                  {data.identity.zsm_mobile && (
                    <a href={`https://wa.me/${(data.identity.zsm_mobile || '').replace(/[^\d]/g,'')}`}
                      target="_blank" rel="noreferrer"
                      className="px-1.5 py-0.5 rounded bg-[#fff7ed] text-[#7c2d12] hover:bg-[#ffedd5] text-[10px]"
                      data-testid="qp-wa-zsm"
                      title="WhatsApp ZSM">
                      💬
                    </a>
                  )}
                  {/* ZH */}
                  {data.identity.zh_name && (
                    <span className="text-[10px] text-soft">
                      · 🎯 ZH <strong className="text-[#15110d]">{data.identity.zh_name}</strong>
                    </span>
                  )}
                  {data.identity.zh_mobile && (
                    <a href={`tel:${data.identity.zh_mobile}`}
                      className="px-1.5 py-0.5 rounded bg-[#ede9fe] text-[#5b21b6] hover:bg-[#ddd6fe] text-[10px] font-mono"
                      data-testid="qp-call-zh"
                      title="Call ZH">
                      📞
                    </a>
                  )}
                  {/* DBR · for reference */}
                  {data.identity.dbr_name && (
                    <span className="text-[10px] text-soft">
                      · 🏢 DBR <strong className="text-[#15110d]">{data.identity.dbr_name}</strong>
                    </span>
                  )}
                </div>
              </div>

              {/* Stat tiles · compact 3-column */}
              <div className="px-5 pt-3 grid grid-cols-3 gap-2">
                <StatTile testId="stat-phone" label="Phone"
                  value={data.identity.phone_primary}
                  hint={data.identity.phone_secondary ? `alt: ${data.identity.phone_secondary}` : null} />
                <StatTile testId="stat-language" label="Language"
                  value={(data.identity.language_pref || 'en').toUpperCase()}
                  hint="concierge in this language" />
                <StatTile testId="stat-engagement" label="Engagement"
                  value={data.identity.feedback_rate_label || '0 of 13'}
                  hint={data.identity.status_label} />
              </div>

              {/* Pinned directive · compact in modal */}
              {data.pinned.has_directive && (
                <div className="mx-5 mt-2 p-2.5 rounded-lg text-[11px]"
                  style={{ background: '#fff7ed', border: '1px solid #fb923c', color: '#7c2d12' }}
                  data-testid="quick-preview-directive">
                  <span className="font-semibold">📌 </span>
                  {data.pinned.concierge_directive.slice(0, 160)}
                  {data.pinned.concierge_directive.length > 160 && '…'}
                </div>
              )}

              {/* Benefit tabs · sticky */}
              <div className="px-5 pt-3 pb-0 flex gap-1 overflow-x-auto border-b border-line"
                data-testid="benefit-tabs">
                {BENEFIT_TABS.map((bt) => {
                  const elig = eligibilityMap[bt.id];
                  const isActive = tab === bt.id;
                  return (
                    <button
                      key={bt.id}
                      onClick={() => setTab(bt.id)}
                      disabled={elig === false}
                      className={`px-3 py-1.5 text-[11px] rounded-t-lg font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? 'bg-[#15110d] text-white'
                          : elig === false
                          ? 'opacity-30 cursor-not-allowed'
                          : 'bg-white text-[#15110d] hover:bg-[#faf6ef] border border-line border-b-0'
                      }`}
                      data-testid={`tab-${bt.id}`}
                    >
                      {bt.icon} {bt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SCROLLABLE BODY · timeline + WA preview */}
            <div
              className="flex-1 min-h-0 overflow-y-auto bg-[#15110d]/[0.02] grid grid-cols-1 md:grid-cols-2 gap-3 p-4"
              data-testid="quick-preview-body"
            >
              <div className="bg-white border border-line rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-wider text-soft mb-3">
                  Journey · {BENEFIT_TABS.find((b) => b.id === tab)?.label}
                </div>

                {/* Curated suggestion for THIS partner × THIS benefit · click ✏ to edit
                    Saved to `preferences_2026.<field>` · audit-logged on every change. */}
                {BENEFIT_PREF_PATH[tab] && (
                  <div
                    className="rounded-lg border p-3 mb-3"
                    style={{ background: '#fffbf0', borderColor: '#fde68a' }}
                    data-testid={`qp-suggestion-${tab}`}
                  >
                    <EditablePref
                      dealerId={data.retailer_id}
                      fieldPath={BENEFIT_PREF_PATH[tab]}
                      label={`Pre-curated for ${data.identity.owner_first_name || 'this partner'}`}
                      value={(data.preferences_2026 || {})[BENEFIT_PREF_PATH[tab].split('.').pop()] || ''}
                      onSaved={(newVal) => {
                        // Optimistic local update so the strip + downstream
                        // surfaces (Compose modal, Ayush page) reflect it immediately.
                        setData((prev) => prev && ({
                          ...prev,
                          preferences_2026: {
                            ...(prev.preferences_2026 || {}),
                            [BENEFIT_PREF_PATH[tab].split('.').pop()]: newVal,
                          },
                        }));
                      }}
                      highlight
                      placeholder="Type the venue / option you've curated for this partner…"
                      testId={`qp-pref-${tab}`}
                    />
                  </div>
                )}

                {activeInstance ? (
                  <>
                    <JourneyTimeline instance={activeInstance} />
                    <div className="mt-3 text-[10px] text-soft border-t border-line pt-2">
                      Step {activeInstance.current_step} · {activeInstance.current_stage_key}
                      {activeInstance.zoho_ticket_id && (
                        <a href={activeInstance.zoho_ticket_url || '#'}
                          target="_blank" rel="noreferrer"
                          className="block mt-1 text-[#1e40af] hover:underline">
                          Zoho ticket #{activeInstance.zoho_ticket_id} ↗
                        </a>
                      )}
                    </div>
                  </>
                ) : eligibilityMap[tab] === false ? (
                  <div className="text-sm text-soft py-8 text-center">
                    Not eligible · this partner's tier doesn't include this benefit.
                  </div>
                ) : (
                  <div className="text-sm text-soft py-8 text-center">
                    No active workflow yet · materialises when the monthly cron fires.
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <WAPreviewPane benefitId={tab} language={data.identity.language_pref}
                  draftOverride={liveDraft} />

                {/* Draft action row · only when a draft is staged */}
                {liveDraft && (
                  <div className="bg-white border border-[#fb923c] rounded-2xl p-3 flex flex-wrap items-center gap-2"
                    data-testid="draft-action-row">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-[#7c2d12] font-bold">
                        ✏️ Draft ready · review above
                      </div>
                      <div className="text-[10px] text-soft mt-0.5">
                        {liveDraft.target_language_name} ·{' '}
                        {liveDraft.translated_text.length} chars · not sent yet
                      </div>
                    </div>
                    <button onClick={() => setLiveDraft(null)}
                      disabled={sendingDraft}
                      className="px-3 py-1.5 rounded-lg border border-line text-[12px] text-soft hover:bg-[#faf6ef] disabled:opacity-50"
                      data-testid="qp-discard-draft-btn">
                      Discard
                    </button>
                    <button onClick={() => setComposerInstance(activeInstance || {
                          _id: `__draft_${data.retailer_id}__${tab}`,
                          dealer_id: data.retailer_id,
                          benefit_id: tab,
                          zoho_ticket_id: null,
                        })}
                      disabled={sendingDraft}
                      className="px-3 py-1.5 rounded-lg border border-line text-[12px] hover:bg-[#faf6ef] disabled:opacity-50"
                      data-testid="qp-edit-draft-btn">
                      ✏️ Edit
                    </button>
                    <button onClick={sendDraftToZoho}
                      disabled={sendingDraft}
                      className="px-4 py-1.5 rounded-lg bg-[#15803d] text-white text-[12px] font-bold hover:bg-[#166534] disabled:opacity-50 flex items-center gap-1"
                      data-testid="qp-send-draft-btn">
                      {sendingDraft ? 'Sending…' : '🚀 Send to Zoho'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* STICKY FOOTER · action toolbar */}
            <div className="flex-shrink-0 px-3 py-2.5 border-t border-line bg-white flex flex-wrap items-center gap-2"
              data-testid="quick-preview-footer">
              <button
                onClick={() => setComposerInstance(activeInstance || {
                  _id: `__draft_${data.retailer_id}__${tab}`,
                  dealer_id: data.retailer_id,
                  benefit_id: tab,
                  zoho_ticket_id: null,
                })}
                className="px-3 py-1.5 rounded-lg bg-[#15110d] text-white text-[12px] font-semibold hover:bg-[#2a2419] flex items-center gap-1"
                data-testid="qp-compose-msg-btn"
                title={activeInstance
                  ? 'Type English · Claude Sonnet 4.5 translates · save to ticket'
                  : 'Compose an ad-hoc message · saves at dealer level + pushes to Zoho if a ticket exists'}
              >
                💬 Compose WA message
              </button>
              <button
                onClick={() => setTalkNowOpen(true)}
                className="px-3 py-1.5 rounded-lg bg-[#0c5b53] text-white text-[12px] font-semibold hover:bg-[#0a4a44] flex items-center gap-1"
                data-testid="qp-talk-now-btn"
                title="Free-form ad-hoc message · type anything · auto-opens a Zoho ticket if none exists"
              >
                🗣 Talk now
              </button>
              <button
                onClick={downloadProfilePdf}
                disabled={downloadingPdf}
                className="px-3 py-1.5 rounded-lg bg-[#15803d] text-white text-[12px] font-semibold hover:bg-[#166534] disabled:opacity-50 flex items-center gap-1"
                data-testid="qp-download-pdf-btn"
                title="A4 partner dossier · all fields, eligibility, escalation chain"
              >
                {downloadingPdf ? 'Generating…' : '⬇ Download PDF'}
              </button>
              <button
                onClick={() => onOpenFull?.(data.retailer_id)}
                className="px-3 py-1.5 rounded-lg border border-[#ff6900] text-[#ff6900] text-[12px] font-semibold hover:bg-[#fff7ed] flex items-center gap-1"
                data-testid="open-full-360-btn"
              >
                Open full 360 →
              </button>
              <div className="ml-auto text-[10px] text-soft">
                {data.panel_counts.feedback} feedback · {data.panel_counts.open_tickets} open tickets
              </div>
            </div>
          </>
        )}

        {composerInstance && (
          <MessageComposerModal
            instance={composerInstance}
            dealerLanguage={data?.identity?.language_pref}
            benefitName={BENEFIT_TABS.find((b) => b.id === tab)?.label}
            initialSource={liveDraft?.source_text}
            initialTarget={liveDraft?.target_language}
            onClose={() => setComposerInstance(null)}
            onDraftReady={(draft) => {
              setLiveDraft({
                source_text: draft.source_text,
                translated_text: draft.translated_text,
                target_language: draft.target_language,
                target_language_name: draft.target_language_name,
                benefit_id: tab,
                benefit_label: BENEFIT_TABS.find((b) => b.id === tab)?.label,
              });
              setComposerInstance(null);
              toast.success(
                'Draft inserted into preview · review and press Send to dispatch',
              );
            }}
          />
        )}

        {/* 🗣 Talk now — free-form ad-hoc message · NO pre-fill · always
            dealer-level · auto-opens a Zoho ticket on send if none exists. */}
        {talkNowOpen && data && (
          <MessageComposerModal
            dealerId={data.retailer_id}
            dealerLanguage={data.identity.language_pref}
            benefitName="Talk now · ad-hoc"
            initialSource=""
            onClose={() => setTalkNowOpen(false)}
            onSaved={() => {
              toast.success('Pushed to Zoho · Tanisha\'s team fires WhatsApp');
              setTalkNowOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
